// Building and opening legacy-portal deep links. The destinations themselves live in
// config/legacyPortal.js — this is the behaviour around them.
import {
  LEGACY_PORTAL_BASE_URL, PORTAL_FALLBACK_URL, LEGACY_ROUTES,
} from '../config/legacyPortal';
import { getLegacyRandomNum } from '../auth/embedded/legacySession';
import { navigateParent } from '../auth/embedded/parentNavigation';

/**
 * Absolute URL for a legacy route key.
 *
 * @param {string} routeKey  a key of LEGACY_ROUTES
 * @param {object} [params]  extra query params, merged over any the route carries itself
 *                           (values are URL-encoded; null/undefined entries are dropped)
 * @returns {string} the deep link, or the participant dashboard's when the key has no path yet
 */
export function buildLegacyUrl(routeKey, params = {}) {
  const route = LEGACY_ROUTES[routeKey];
  // A destination is a bare path, `{ path, params }` for a page addressed by a query
  // param too, or `{ path, method, fields }` for a form POST (see LEGACY_ROUTES). Every
  // shape reduces to a path + the route's own params.
  const isDetailed = route !== null && typeof route === 'object';
  const path = isDetailed ? route.path : route;
  const routeParams = isDetailed ? route.params : null;

  if (route === undefined && process.env.NODE_ENV !== 'production') {
    // A typo'd key would silently send the participant to the login page.
    // eslint-disable-next-line no-console
    console.warn(`[legacyPortal] unknown route key "${routeKey}" — add it to LEGACY_ROUTES.`);
  }
  // No path supplied by the .NET team yet: the participant's legacy dashboard, built like
  // any other route so it carries RANDOMNUM.
  if (!path) return routeKey === 'participantDashboard' ? PORTAL_FALLBACK_URL : buildLegacyUrl('participantDashboard');

  const query = new URLSearchParams();
  // The legacy pages link to each other with `?RANDOMNUM=<stamp>`, and produce the
  // literal string "undefined" when they have no stamp to hand. We reproduce that
  // exactly rather than omitting the param: "RANDOMNUM=undefined" is a URL the legacy
  // app is known to serve, an absent param is not.
  query.set('RANDOMNUM', getLegacyRandomNum() ?? 'undefined');
  // The route's own params first, then the caller's — so a call site can still override
  // one for a specific hand-off without editing the map.
  Object.entries({ ...routeParams, ...params }).forEach(([k, v]) => {
    if (v !== null && v !== undefined) query.set(k, String(v));
  });

  return `${LEGACY_PORTAL_BASE_URL}${path}?${query.toString()}`;
}

/**
 * Send the participant to a legacy route IN THE SAME PAGE. Inside the iframe the parent
 * navigates its own top-level window when it supports that; otherwise we target `_top`
 * ourselves, which takes the whole tab (the framing portal included) to the legacy page.
 * Standalone, `_top` is this window, so it is a plain redirect.
 *
 * Must be called straight from a click handler — top navigation from a frame needs the
 * user gesture.
 *
 * @returns {string} the URL that was opened (handy for tests and logging)
 */
export function openLegacy(routeKey, params) {
  const url = buildLegacyUrl(routeKey, params);
  const route = LEGACY_ROUTES[routeKey];
  // A POST route (OutBoundSAML) needs its form body, so it can only go as a form submit
  // to `_top` — the parent's `pcs:navigate` is a plain GET and cannot carry it.
  if (route && typeof route === 'object' && route.method === 'POST' && url !== PORTAL_FALLBACK_URL) {
    postTop(url, route.fields);
    return url;
  }
  if (!navigateParent(url)) {
    window.open(url, '_top');
  }
  return url;
}

/** Submit `fields` to `url` as a POST that replaces the whole tab. */
function postTop(url, fields = {}) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.target = '_top';
  form.hidden = true;
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(value);
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  form.remove();
}

export default openLegacy;
